/**
 * @file ProcessorSettings.js
 * @version 1.1.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-09-18
 * @modified 2026-09-18
 * @description The Settings tab of the Map of Ag report: the list of milk
 *              processors the office maintains.
 *
 *              A processor with contacts assigned to it is DEACTIVATED rather
 *              than deleted, and the server decides which happens — reports
 *              have gone out under that name, and the run history has to keep
 *              pointing at something real. The button therefore says what will
 *              happen before it happens, rather than offering "delete" and
 *              quietly doing something else.
 *
 *              Product mapping moved to its own tab — see
 *              FormulationMappingSettings.
 */

import { useState } from 'react';
import { API_URL } from '../config';
import './InvoiceDetailReport.css';
import './MapOfAgReport.css';
import Input from './Input';
import AlertDialog from './AlertDialog';
import ConfirmDialog from './ConfirmDialog';
import TickCheckbox from './TickCheckbox';
import RowActionsMenu from './RowActionsMenu';
import TableContextMenu from './TableContextMenu';
import useTableContextMenu from '../hooks/useTableContextMenu';
import { useSlideOutButton } from './SlideOutButton';

function ProcessorSettings({ processors = [], onChanged }) {
  const [newName, setNewName] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [alert, setAlert] = useState({ isOpen: false, message: '', type: 'info' });

  useSlideOutButton([processors.length, editingId, saving]);

  const say = (message, type = 'info') => setAlert({ isOpen: true, message, type });
  const ctxMenu = useTableContextMenu({ rows: processors, getRowId: (p) => p.id });


  const call = async (url, options) => {
    setSaving(true);
    try {
      const res = await fetch(url, { credentials: 'include', ...options });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'That did not work');
      await onChanged?.();
      return data;
    } catch (e) {
      say(e.message, 'error');
      return null;
    } finally {
      setSaving(false);
    }
  };

  const add = async () => {
    const name = newName.trim();
    if (!name) return say('Give the processor a name first.', 'warning');
    const done = await call(`${API_URL}/api/map-of-ag/processors`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name })
    });
    if (done) { setNewName(''); say(`${name} added.`, 'success'); }
  };

  const saveEdit = async (p) => {
    const name = editName.trim();
    if (!name || name === p.name) { setEditingId(null); return; }
    const done = await call(`${API_URL}/api/map-of-ag/processors/${p.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name })
    });
    if (done) setEditingId(null);
  };

  const toggleActive = (p) => call(`${API_URL}/api/map-of-ag/processors/${p.id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ isActive: !p.is_active })
  });

  const remove = (p) => {
    setConfirm({
      title: p.contacts > 0 ? 'Deactivate this processor?' : 'Delete this processor?',
      message: p.contacts > 0
        ? `${p.name} has ${p.contacts} contact${p.contacts === 1 ? '' : 's'} assigned to it, and reports have gone out under that name. It will be deactivated and taken out of the picker, not deleted.`
        : `${p.name} has no contacts assigned. It will be deleted.`,
      confirmText: p.contacts > 0 ? 'Deactivate' : 'Delete',
      type: 'warning',
      onConfirm: async () => {
        setConfirm(null);
        const done = await call(`${API_URL}/api/map-of-ag/processors/${p.id}`, { method: 'DELETE' });
        if (done) say(done.deactivated ? `${p.name} deactivated.` : `${p.name} deleted.`, 'success');
      }
    });
  };

  /** The same actions behind the three dots and the right-click menu. */
  const buildRowActions = (p) => [
    {
      key: 'rename',
      icon: 'edit',
      label: 'Rename',
      onClick: () => { setEditingId(p.id); setEditName(p.name); }
    },
    {
      key: 'active',
      icon: p.is_active ? 'visibility_off' : 'visibility',
      label: p.is_active ? 'Deactivate' : 'Activate',
      title: p.is_active ? 'Take it out of the picker' : 'Offer it in the picker again',
      onClick: () => toggleActive(p)
    },
    {
      key: 'remove',
      icon: 'delete',
      label: p.contacts > 0 ? 'Deactivate (has contacts)' : 'Delete',
      title: p.contacts > 0
        ? `${p.contacts} contacts are assigned — this deactivates rather than deletes`
        : 'Delete this processor',
      onClick: () => remove(p)
    }
  ];

  return (
    <div className="map-of-ag-report invoice-detail-report processor-settings">
      <div className="search-header">
        <div className="workflow-info" style={{ gridColumn: '1' }}>
          <span className="info">Processors</span>
          <span className="note">
            <span className="material-symbols-rounded">info</span>
            <span style={{ margin: 'auto 0' }}>
              The dairies and schemes contacts are reported to. Used by the Contacts tab and on every report.
            </span>
          </span>
        </div>
      </div>

      <div className="processor-add">
        <Input
          type="text"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') add(); }}
          placeholder="New processor name…"
        />
        <div className="buttons slideout">
          <div>
            <button className="btn-slideout" onClick={add} disabled={saving}>
              <div className="icon-wrapper"><span className="material-symbols-rounded">add</span></div>
              <span className="btn-label">Add processor</span>
            </button>
          </div>
        </div>
      </div>

      <div className="table-container">
        <table className="data-table processor-table">
          <thead>
            <tr>
              <th>Processor</th>
              <th className="num">Contacts</th>
              <th>Active</th>
              <th className="actions-header no-sort" />
            </tr>
          </thead>
          <tbody>
            {processors.map((p) => (
              <tr key={p.id} className={`line-row${p.is_active ? '' : ' is-inactive'}`}>
                <td>
                  {editingId === p.id ? (
                    <Input
                      type="text"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') saveEdit(p);
                        if (e.key === 'Escape') setEditingId(null);
                      }}
                      /* The tick and cross went with the action buttons, so
                         clicking away has to commit — otherwise a rename is
                         stranded in a box with no way out but the keyboard. */
                      onBlur={() => saveEdit(p)}
                      autoFocus
                    />
                  ) : p.name}
                </td>
                <td className="num">{p.contacts}</td>
                <td>
                  <TickCheckbox
                    id={`proc-active-${p.id}`}
                    checked={!!p.is_active}
                    onChange={() => toggleActive(p)}
                    ariaLabel={`${p.is_active ? 'Deactivate' : 'Activate'} ${p.name}`}
                  />
                </td>
                <td>
                  <div className="actions-cell">
                    <RowActionsMenu actions={buildRowActions(p)} />
                  </div>
                </td>
              </tr>
            ))}
            {!processors.length && (
              <tr><td colSpan={4} className="report-empty">No processors yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <TableContextMenu
        ctx={ctxMenu}
        rowActions={buildRowActions}
        actions={[
          {
            key: 'add',
            icon: 'add',
            label: 'Add processor',
            title: 'Type a name in the box above',
            onClick: () => document.querySelector('.processor-add input')?.focus()
          },
          { key: 'refresh', readSafe: true, icon: 'refresh', label: 'Refresh', onClick: onChanged }
        ]}
      />

      {confirm && <ConfirmDialog isOpen {...confirm} onCancel={() => setConfirm(null)} />}
      <AlertDialog
        isOpen={alert.isOpen}
        message={alert.message}
        type={alert.type}
        onClose={() => setAlert({ ...alert, isOpen: false })}
      />
    </div>
  );
}

export default ProcessorSettings;
